import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { NavigationBar, NavigationConfigs } from '@/components/layout';
import { RestaurantModal, MenuExportModal, MenuImportModal } from '@/components/restaurants';
import { AccountModal } from '@/components/account';
import useAuthStore from '@/lib/stores/authStore';
import useRestaurantStore from '@/lib/stores/restaurantStore';
import { 
  Plus, 
  Store, 
  Edit, 
  Trash2,
  Menu as MenuIcon,
  Image as ImageIcon,
  QrCode,
  Download,
  Upload
} from 'lucide-react';
import { ROUTES } from '@/types';
import type { Restaurant } from '@/types';

/**
 * RestaurantsPage - Main restaurants management page
 * 
 * Features:
 * - List all user restaurants in a table
 * - Add new restaurant (max 1 for trial users)
 * - Edit, hide/show, delete restaurants
 * - Navigate to menu management
 * - Show subscription limits
 */
const RestaurantsPage: React.FC = () => {
  const { user, accessState, checkAccessState } = useAuthStore();
  const { restaurants, loading, error, fetchRestaurants, deleteRestaurant, clearError } = useRestaurantStore();

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [editingRestaurant, setEditingRestaurant] = useState<Restaurant | null>(null);
  
  // Export/Import modal state
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null);

  // Account modal state for expired access
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);

  // Fetch restaurants and check access on mount
  useEffect(() => {
    if (user?.uid) {
      clearError();
      fetchRestaurants(user.uid);
      checkAccessState();
    }
  }, [user?.uid, fetchRestaurants, clearError, checkAccessState]);

  // Auto-open account modal if access is expired
  useEffect(() => {
    if (accessState?.isExpired && !isAccountModalOpen) {
      setIsAccountModalOpen(true);
    }
  }, [accessState?.isExpired, isAccountModalOpen]);

  const handleDelete = async (restaurantId: string, restaurantName: string) => {
    if (window.confirm(`Are you sure you want to delete "${restaurantName}"? This action cannot be undone.`)) {
      try {
        await deleteRestaurant(restaurantId);
      } catch (error) {
        // Error handled by store
      }
    }
  };

  const handleAddRestaurant = () => {
    setModalMode('add');
    setEditingRestaurant(null);
    setIsModalOpen(true);
  };

  const handleEditRestaurant = (restaurant: Restaurant) => {
    setModalMode('edit');
    setEditingRestaurant(restaurant);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRestaurant(null);
  };

  const handleExportMenu = (restaurant: Restaurant) => {
    setSelectedRestaurant(restaurant);
    setIsExportModalOpen(true);
  };

  const handleImportMenu = (restaurant: Restaurant) => {
    setSelectedRestaurant(restaurant);
    setIsImportModalOpen(true);
  };

  const handleCloseExportModal = () => {
    setIsExportModalOpen(false);
    setSelectedRestaurant(null);
  };

  const handleCloseImportModal = () => {
    setIsImportModalOpen(false);
    setSelectedRestaurant(null);
  };

  const handleImportComplete = () => {
    // Refresh restaurant data after import
    if (user?.uid) {
      fetchRestaurants(user.uid);
    }
  };

  // Trial user can only have 1 restaurant, and access must be active
  const isAtRestaurantLimit = restaurants.length >= 1;
  const canAddRestaurant = !isAtRestaurantLimit && accessState?.hasAccess;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <NavigationBar config={NavigationConfigs.restaurants()} />

      {/* Main Content */}
      <main className="max-w-12xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 sm:mb-8 space-y-4 sm:space-y-0">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Restaurant Management</h1>
            <p className="text-gray-600 mt-1 sm:mt-2 text-sm sm:text-base">
              Manage your restaurants and their digital menus
            </p>
          </div>
        </div>

        {/* Error Display - Only show for actual errors, not empty states */}
        {error && restaurants.length === 0 && !loading && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
          >
            <p className="text-red-800 text-sm font-medium">
              {error}
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => clearError()}
              className="mt-2"
            >
              Dismiss
            </Button>
          </motion.div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-8 sm:py-12">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 animate-pulse">
              <Store className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <p className="text-gray-600 text-sm sm:text-base">Loading your restaurants...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && restaurants.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12 sm:py-16"
          >
            <div className="w-16 h-16 sm:w-24 sm:h-24 bg-primary rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <Store className="w-8 h-8 sm:w-12 sm:h-12 text-white" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
              Ready to create your first restaurant?
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
              Set up your restaurant profile and start building your digital menu. Your customers will love the contactless dining experience.
            </p>
            
            {/* Add Restaurant Button */}
            {(!canAddRestaurant || loading) ? (
              <Button
                variant="primary"
                leftIcon={<Plus className="w-4 h-4" />}
                disabled={true}
                size="lg"
                className="mx-auto"
                title={
                  !accessState?.hasAccess 
                    ? "Access expired - extend to add restaurants" 
                    : isAtRestaurantLimit 
                      ? "Maximum restaurants reached for your plan"
                      : "Loading..."
                }
              >
                Add Restaurant
              </Button>
            ) : (
              <Button
                variant="primary"
                leftIcon={<Plus className="w-4 h-4" />}
                onClick={handleAddRestaurant}
                size="lg"
                className="mx-auto"
              >
                Add Restaurant
              </Button>
            )}
          </motion.div>
        )}

        {/* Restaurants Cards */}
        {!loading && restaurants.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6"
          >
            {restaurants.map((restaurant) => (
              <motion.div
                key={restaurant.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-md transition-shadow"
              >
                {/* Restaurant Logo and Name */}
                <div className="flex flex-col items-center text-center mb-4 sm:mb-6">
                  {/* Logo */}
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden mb-3 sm:mb-4 bg-gray-100 flex items-center justify-center">
                    {restaurant.logoUrl ? (
                      <img 
                        src={restaurant.logoUrl} 
                        alt={`${restaurant.name} logo`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fallback to default icon if image fails to load
                          const target = e.currentTarget;
                          target.style.display = 'none';
                          const parent = target.parentElement!;
                          const fallback = parent.querySelector('.fallback-icon');
                          if (fallback) {
                            (fallback as HTMLElement).style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    {/* Fallback icon - shown when no image or image fails */}
                    <div 
                      className={`w-12 h-12 sm:w-16 sm:h-16 bg-primary rounded-xl flex items-center justify-center fallback-icon ${
                        restaurant.logoUrl ? 'hidden' : 'flex'
                      }`}
                    >
                      <Store className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                    </div>
                  </div>
                  
                  {/* Restaurant Name */}
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 line-clamp-2 leading-tight">
                    {restaurant.name}
                  </h3>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  {/* All action buttons in a single row */}
                  <div className="flex items-center justify-center space-x-2 mb-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Edit Restaurant"
                      onClick={() => handleEditRestaurant(restaurant)}
                      className="p-2"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Export Menu"
                      onClick={() => handleExportMenu(restaurant)}
                      className="p-2"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Import Menu"
                      onClick={() => handleImportMenu(restaurant)}
                      className="p-2"
                    >
                      <Upload className="w-4 h-4" />
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(restaurant.id, restaurant.name)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 p-2"
                      title="Delete Restaurant"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <Link to={`${ROUTES.IMAGE_MENU.replace(':id', restaurant.id)}`}>
                    <Button
                      variant="primary"
                      size="sm"
                      leftIcon={<ImageIcon className="w-4 h-4" />}
                      className="w-full mb-3"
                    >
                      Image Menu
                    </Button>
                  </Link>
                  
                  <Link to={`${ROUTES.MENU_BUILDER.replace(':id', restaurant.id)}`}>
                    <Button
                      variant="primary"
                      size="sm"
                      leftIcon={<MenuIcon className="w-4 h-4" />}
                      className="w-full mb-3"
                    >
                      Digital Menu
                    </Button>
                  </Link>
                  
                  <Link to={`${ROUTES.QR_CODE_MANAGEMENT.replace(':id', restaurant.id)}`}>
                    <Button
                      variant="primary"
                      size="sm"
                      leftIcon={<QrCode className="w-4 h-4" />}
                      className="w-full"
                    >
                      QR Codes
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </main>

      {/* Restaurant Modal */}
      <RestaurantModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        mode={modalMode}
        restaurant={editingRestaurant}
      />

      {/* Export Modal */}
      {selectedRestaurant && (
        <MenuExportModal
          isOpen={isExportModalOpen}
          onClose={handleCloseExportModal}
          restaurant={selectedRestaurant}
        />
      )}

      {/* Import Modal */}
      {selectedRestaurant && (
        <MenuImportModal
          isOpen={isImportModalOpen}
          onClose={handleCloseImportModal}
          restaurant={selectedRestaurant}
          onImportComplete={handleImportComplete}
        />
      )}

      {/* Account Modal - for expired access */}
      <AccountModal
        isOpen={isAccountModalOpen}
        onClose={() => setIsAccountModalOpen(false)}
        canClose={!accessState?.isExpired}
      />
    </div>
  );
};

export default RestaurantsPage;
